import React, { useContext, useState, useEffect, useRef } from 'react';
import { ShopContext } from '../context/shopcontext';
import { Heart, Star, StarHalf, ShoppingBag, Crown, RotateCcw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import ProductItemSkeleton from './ProductItemSkeleton';

const ProductItem = ({
  id,
  image,
  name,
  price,
  rating,
  totalReviews,
  bestseller,
  restock,
  inStock,
  sizes,
  stockQuantity,
  category,
  currentPage,
  categoryFilter,
  subCategoryFilter,
  sortType,
  pageKey = '',
  fromRelated = false,
}) => {
  const { wishlist, addToWishlist, removeFromWishlist, token, addToCart, backendUrl } =
    useContext(ShopContext);
  const currency = '₹';
  const navigate = useNavigate();
  const isInWishlist = wishlist.some((item) => item._id === id);
  const [showSizes, setShowSizes] = useState(false);
  const [animateCart, setAnimateCart] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const sizePopupRef = useRef(null);
  const [animateWishlist, setAnimateWishlist] = useState(false);

  const isOutOfStock =
    !sizes || sizes.length === 0
      ? stockQuantity === 0 || !inStock
      : sizes.every((size) => size.quantity === 0) || !inStock;

  const handleNavigation = () => {
    if (!fromRelated) {
      if (pageKey) {
        sessionStorage.setItem(`${pageKey}ScrollPosition`, window.scrollY);
      }

      if (typeof currentPage !== 'undefined') sessionStorage.setItem('currentPage', currentPage);
      if (Array.isArray(categoryFilter))
        sessionStorage.setItem('categoryFilter', JSON.stringify(categoryFilter));
      if (Array.isArray(subCategoryFilter))
        sessionStorage.setItem('subCategoryFilter', JSON.stringify(subCategoryFilter));
      if (typeof sortType !== 'undefined') sessionStorage.setItem('sortType', sortType);
    }

    const viewedProduct = {
      id,
      image: image[0],
      name,
      price,
      category,
      type: 'recentlyViewed',
    };

    try {
      const existingViewed = JSON.parse(sessionStorage.getItem('recentlyViewedProducts')) || [];
      const filtered = existingViewed.filter((item) => item.id !== id);
      const updatedViewed = [viewedProduct, ...filtered].slice(0, 10);
      sessionStorage.setItem('recentlyViewedProducts', JSON.stringify(updatedViewed));
    } catch (error) {
      console.error('Error saving recently viewed products:', error);
    }

    navigate(`/product/${id}`);
  };

  useEffect(() => {
    let timeout;

    function handleClickOutside(event) {
      if (sizePopupRef.current && !sizePopupRef.current.contains(event.target)) {
        setShowSizes(false);
      }
    }

    if (showSizes) {
      timeout = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 0);
    }

    return () => {
      clearTimeout(timeout);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSizes]);

  const handleAddToCart = async (productId, size, token) => {
    if (!productId) {
      toast.error('Invalid product ID.');
      return;
    }

    setAnimateCart(true);
    setTimeout(() => setAnimateCart(false), 300);

    await addToCart(productId, size || 'default');

    if (!token) {
      toast.success('Product added to cart!', {
        autoClose: 3000,
      });
      setTimeout(() => setShowSizes(false), 300);
    } else {
      setShowSizes(false);
    }
  };

  const renderStars = () => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(<Star key={i} className="w-3 h-3 text-amber-500 fill-amber-500" />);
      } else if (i === fullStars && hasHalfStar) {
        stars.push(<StarHalf key={i} className="w-3 h-3 text-amber-500 fill-amber-500" />);
      } else {
        stars.push(<Star key={i} className="w-3 h-3 text-slate-300" />);
      }
    }
    return stars;
  };

  return (
    <div
      className="group relative bg-white border border-slate-100 transition-all duration-500 ease-out cursor-pointer hover:border-slate-200 rounded-md h-[400px] md:h-[550px] flex flex-col"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleNavigation}
      role="button"
      tabIndex="0"
      aria-label={`View details of ${name}`}
      style={{
        boxShadow: isHovered
          ? '0 20px 40px -8px rgba(0, 0, 0, 0.08), 0 8px 16px -4px rgba(0, 0, 0, 0.04)'
          : '0 1px 3px 0 rgba(0, 0, 0, 0.02)',
      }}
    >
      {/* Image Container - Fixed height */}
      <div className="relative h-[260px] md:h-[400px] overflow-hidden bg-slate-50 rounded-md flex-shrink-0">
        <img
          loading="lazy"
          className={`w-full h-full object-cover transition-all duration-700 ${
            isHovered ? 'scale-105' : 'scale-100'
          } ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
          src={image[0]}
          alt={name}
          onLoad={() => setImageLoaded(true)}
        />

        {/* Loading skeleton */}
        {!imageLoaded && <div className="absolute inset-0 bg-slate-100 animate-pulse"></div>}

        {/* Subtle overlay on hover */}
        <div
          className={`absolute inset-0 bg-black transition-opacity duration-500 ${
            isHovered ? 'opacity-5' : 'opacity-0'
          }`}
        ></div>

        {/* Top badges and wishlist */}
        <div className="absolute top-1 left-1 right-1 flex items-start justify-between">
          <div className="flex items-start">
            {bestseller && (
              <div className="flex items-center gap-1.5 bg-[#4c0f2e] text-[#e0c3b5] px-3 py-1.5 text-xs font-medium tracking-wide rounded-md shadow-md">
                <Crown className="w-3 h-3" />
                BESTSELLER
              </div>
            )}
          </div>

          {/* Wishlist button - always positioned on the right */}
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!token) {
                navigate('/login');
              } else {
                setAnimateWishlist(true);
                setTimeout(() => setAnimateWishlist(false), 300);
                isInWishlist ? removeFromWishlist(id) : addToWishlist(id);
              }
            }}
            className="bg-[#e0c3b5] backdrop-blur-sm border rounded-full border-[#4c0f2e] p-2 transition-all shadow-lg duration-300 hover:bg-[#e0c3b5] hover:shadow-md"
            aria-label={isInWishlist ? 'Remove from wishlist' : 'Add to wishlist'}
          >
            <Heart
              className={`w-4 h-4 transition-all duration-300 ${
                isInWishlist
                  ? 'text-[#4c0f2e] fill-[#4c0f2e]'
                  : 'text-[#4c0f2e] fill-none bg-[#e0c3b5]'
              } ${animateWishlist ? 'scale-110' : 'scale-100'}`}
            />
          </button>
        </div>

        {/* Bottom status badges */}
        <div className="absolute bottom-1 right-1">
          {isOutOfStock && (
            <div className="bg-[#4c0f2e] rounded-md text-[#e0c3b5] px-3 py-1.5 text-xs font-medium tracking-wide">
              SOLD OUT
            </div>
          )}

          {restock && !isOutOfStock && (
            <div className="flex items-center rounded-md gap-1.5 bg-[#e0c3b5] text-[#4c0f2e] border border-[#4c0f2e] px-3 py-1.5 text-xs  tracking-wide">
              <RotateCcw className="w-3 h-3" />
              RESTOCKED
            </div>
          )}
        </div>
      </div>

      {/* Product Information - Fixed height with flex layout */}
      <div className="p-4 flex flex-col justify-between flex-1 min-h-[120px]">
        <div className="space-y-2">
          {/* Category */}
          <p className="text-[#4c0f2e] text-xs uppercase tracking-widest font-medium">{category}</p>

          {/* Product Name - Fixed height with ellipsis */}
          <h3 className="text-slate-900 text-sm font-medium leading-tight h-[30px] overflow-hidden line-clamp-1">
            {name}
          </h3>
        </div>

        <div className="space-y-1 mt-2 ">
          {/* Rating - Fixed position */}
          {totalReviews > 0 && (
            <div className="flex items-center gap-2 ">
              <div className="flex items-center gap-0.5">{renderStars()}</div>
              <span className="text-slate-600 text-xs">
                {rating.toFixed(1)} ({totalReviews})
              </span>
            </div>
          )}

          {/* Price - Fixed at bottom */}
          <div className="flex items-baseline gap-3">
            <span className="text-[#4c0f2e] font-semibold text-lg">
              {currency}
              {price.toLocaleString('en-IN')}
            </span>
            <span className="text-slate-400 text-sm line-through">
              {currency}
              {Math.round(price * 1.2).toLocaleString('en-IN')}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Enhanced CSS with fixed dimensions
const styles = `
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.backdrop-blur-sm {
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
}

/* Smooth transitions */
* {
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
}

/* Ensure consistent card heights in grid layouts */
.product-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
  align-items: stretch;
}

/* Additional responsive breakpoints for fixed cards */
@media (max-width: 640px) {
  .product-item-fixed {
    height: 380px;
  }
  .product-item-fixed .image-container {
    height: 220px;
  }
  .product-item-fixed .content-container {
    min-height: 140px;
  }
}
`;

// Inject styles
if (typeof document !== 'undefined') {
  const existingStyle = document.getElementById('product-item-styles');
  if (existingStyle) {
    existingStyle.remove();
  }

  const styleSheet = document.createElement('style');
  styleSheet.id = 'product-item-styles';
  styleSheet.type = 'text/css';
  styleSheet.innerText = styles;
  document.head.appendChild(styleSheet);
}

export default ProductItem;
